<template>
  <dd-modal
    :title="title"
    :extend-height="requiresStatusMapping"
    :subtitle="subTitle"
    class="bulk-move-items-modal"
    @handle-close="$emit('edit-canceled')"
  >
    <loading-placeholder v-if="isLoading" :lines="20" />
    <template v-else>
      <el-form label-position="top">
        <!-- Entities -->
        <el-form-item label="Project">
          <single-select
            v-model="selectedEntityID" 
            :placeholder="`Select new ${TextAssistant.entitySingularName(entityType).toLowerCase()}...`"
          >
            <el-option
              v-for="entity in entities"
              :key="entity.id"
              :label="entity.title"
              :value="entity.id"
            >
              <i :class="`ddi ddi-${getEntityIcon(entity)}`" />
              <span>{{ entity.title }}</span>
            </el-option>
          </single-select>
        </el-form-item>
        <div v-if="requiresStatusMapping" class="status-mapping-header">
          <p>
            Map task statuses to this project's workflow.
            <el-tooltip placement="bottom">
              <template #content>
                <div>
                  Because this project has a <strong>different workflow</strong> than your current project, 
                  the statuses (and any previous status updates) on these tasks must be updated to
                  statuses in the new project's workflow.
                </div>
              </template>
              <a href="#" class="just-a-link" @click.prevent>
                What is this?
              </a>
            </el-tooltip>
          </p>
        </div>
        <div v-show="requiresStatusMapping">
          <entity-workflow-status-mapper
            v-if="requiresStatusMapping"
            v-model:status-mappings="statusMappings"
            :current-workflow="currentWorkflow"
            :new-workflow="newWorkflow"
            class="bulk-move-items-modal__status-mapper"
          />
        </div>
      </el-form>
    </template>
    <!-- FOOTER -->
    <template v-if="!isLoading" #footer>
      <span>
        <dd-button type="text" @click="$emit('edit-canceled')">Cancel</dd-button>
        <dd-button
          :is-disabled="disableSubmit"
          :is-loading="isSubmitting"
          :color="ColorAssistant.getEntityColor(entityType)"
          @click="handleUpdate"
        >Move to another {{TextAssistant.entitySingularName(entityType).toLowerCase()}}</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IWorkflowForSelection,
  IWorkflowProjectForSelection,
} from '@/api/types/response.types'
import {
  IConvosInputBulkEditMailbox,
  IStatusMapping,
  ITasksInputBulkEditInternalProject,
} from '@/api/types/request.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import TasksAPI from '@/api/modules/tasks.api'
import ConversationsAPI from '@/api/modules/convos.api'

export default defineComponent({
  name: 'BulkMoveItemsModal',
  props: {
    entityId: {
      type: Number,
      required: true,
    },
    selectedItemIds: {
      type: Array as () => number[],
      default: (): number[] => [],
    },
    isConvoRelated: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['edit-canceled', 'edit-completed'],
  data() {
    return {
      TextAssistant,
      ColorAssistant,
      EEntities,
      isLoading: false,
      isSubmitting: false,
      comment: '',

      // Projects
      workflowID: 0,
      entities: [] as IWorkflowProjectForSelection[],
      workflows: [] as IWorkflowForSelection[],
      selectedEntityID: null as any,
      requiresStatusMapping: false,
      statusMappings: [] as IStatusMapping[],
    }
  },
  computed: {
    entityType(): EEntities {
      return this.isConvoRelated ? EEntities.MAILBOXES : EEntities.PROJECTS
    },
    title(): string {
      return `Move to another ${TextAssistant.entitySingularName(
        this.entityType,
      ).toLowerCase()}`
    },
    subTitle(): string {
      if (this.selectedItemIds.length > 1) {
        return `Anyone assigned to these ${TextAssistant.itemPluralName(
          this.entityType,
        ).toLowerCase()} will be notified of your change.`
      } else {
        return `Anyone assigned to this ${TextAssistant.itemSingularName(
          this.entityType,
        ).toLowerCase()} will be notified of your change.`
      }
    },
    disableSubmit(): boolean {
      return (
        this.selectedEntityID === null ||
        (this.requiresStatusMapping && !this.allStatusesMapped)
      )
    },
    currentWorkflow(): IWorkflowForSelection {
      return this.workflows.find(w => w.id === this.workflowID)
    },
    newWorkflow(): IWorkflowForSelection {
      const newWorkflowID = this.entities.find(
        p => p.id === this.selectedEntityID,
      ).workflowID
      return this.workflows.find(w => w.id === newWorkflowID)
    },
    allStatusesMapped(): boolean {
      return (
        this.statusMappings.length > 0 &&
        this.statusMappings.filter(m => m.newStatusID === null).length === 0
      )
    },
  },
  watch: {
    selectedEntityID: {
      immediate: false,
      handler(val: number) {
        const sameWorkflow =
          this.entities.find(p => p.id === val).workflowID === this.workflowID
        this.requiresStatusMapping = !sameWorkflow
      },
    },
  },
  async created() {
    this.isLoading = true
    await this.loadInitialState()
    this.isLoading = false
  },
  methods: {
    getEntityIcon(entity: IWorkflowProjectForSelection) {
      if (entity.isFavorite) {
        return 'star-filled'
      }
      return this.entityType === EEntities.MAILBOXES ? 'mailbox' : 'project'
    },
    async loadInitialState() {
      if (this.isConvoRelated) {
        this.workflows = await MailboxesAPI.get_Workflows()
        const updatableInternalProjectsInfo = await MailboxesAPI.get_UpdatableMailboxes(
          this.entityId,
        )
        this.workflowID = updatableInternalProjectsInfo.workflowID
        this.entities = updatableInternalProjectsInfo.listMailboxes
      } else {
        this.workflows = await InternalProjectsAPI.get_Workflows()
        const updatableInternalProjectsInfo = await InternalProjectsAPI.get_UpdatableInternalProjects(
          this.entityId,
        )
        this.workflowID = updatableInternalProjectsInfo.workflowID
        this.entities = updatableInternalProjectsInfo.listInternalProjects
      }
      return
    },
    async handleUpdate() {
      this.isSubmitting = true
      let submitSucceeded = false

      if (this.isConvoRelated) {
        const data = {
          listConversationIDs: this.selectedItemIds,
          newMailboxID: this.selectedEntityID,
          listStatusMappings: this.statusMappings,
        } as IConvosInputBulkEditMailbox
        try {
          const response = await ConversationsAPI.put_BulkEdit_Mailbox(data)
          this.$messaging.showSuccess(response.message)
          submitSucceeded = true
        } catch (error) {
          this.$error.emit(error, true)
        }
      } else {
        const data = {
          listTaskIDs: this.selectedItemIds,
          newInternalProjectID: this.selectedEntityID,
          listStatusMappings: this.statusMappings,
        } as ITasksInputBulkEditInternalProject
        try {
          const response = await TasksAPI.put_BulkEdit_InternalProject(data)
          this.$messaging.showSuccess(response.message)
          submitSucceeded = true
        } catch (error) {
          this.$error.emit(error, true)
        }
      }
      this.isSubmitting = false
      // Do update...
      if (submitSucceeded) {
        this.$emit('edit-completed', this.selectedEntityID)
      }
    },
  },
})
</script>

<style lang="scss">
.bulk-move-items-modal {
  &__status-mapper {
    width: 100%;
    margin-bottom: 20px;
  }

  .status-mapping-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    p {
      padding: 0 10px;
      background-color: var(--color-background-header);
      z-index: 2;
    }
    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      border-top: 1px solid $color--gray-30;
      background: $color--gray-30;
      width: 100%;
      transform: translateY(-50%);
    }
  }
}
</style>
